'use client';

import { isChunkLoadError, shouldReloadForChunkError } from '@/lib/observability/chunkError';
import { addNextjsError } from '@datadog/browser-rum-nextjs';
import { AlertCircle, ExternalLink, Home, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';

export default function Error({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  const [isInAppBrowser, setIsInAppBrowser] = useState(false);
  const [browserName, setBrowserName] = useState<string | null>(null);

  const chunkError = isChunkLoadError(error);

  useEffect(() => {
    // Stack, digest e componentStack vão para o RUM via addNextjsError; dd_context marca o boundary
    // de página e a falha de chunk para consulta no Error Tracking.
    (error as Error & { dd_context?: Record<string, unknown> }).dd_context = {
      errorBoundary: 'page',
      route: window.location.pathname,
      isChunkLoadError: chunkError,
    };
    addNextjsError(error);

    if (process.env.NODE_ENV !== 'production') {
      console.error('[PAGE-ERROR]', error.name, error.message, { digest: error.digest, chunkError });
    }

    if (chunkError && shouldReloadForChunkError()) {
      window.location.reload();
      return;
    }

    const userAgent = navigator.userAgent || '';

    if (/Instagram/i.test(userAgent)) {
      setIsInAppBrowser(true);
      setBrowserName('Instagram');
    } else if (/FBAN|FBAV|FB_IAB|FBIOS/i.test(userAgent)) {
      setIsInAppBrowser(true);
      setBrowserName('Facebook');
    } else if (/TikTok/i.test(userAgent)) {
      setIsInAppBrowser(true);
      setBrowserName('TikTok');
    } else if (/Twitter/i.test(userAgent)) {
      setIsInAppBrowser(true);
      setBrowserName('Twitter');
    } else if (/LinkedIn/i.test(userAgent)) {
      setIsInAppBrowser(true);
      setBrowserName('LinkedIn');
    }
  }, [error]);

  const handleReportError = () => {
    const subject = encodeURIComponent('Erro na Bilheteria MASP');
    const body = encodeURIComponent(
      `Olá,\n\nEncontrei um erro ao usar a bilheteria:\n\nCódigo do erro: ${error.digest || 'Não disponível'}\nMensagem: ${error.message || 'Erro desconhecido'}\nNavegador: ${isInAppBrowser ? `${browserName} (in-app)` : 'Regular browser'}\n\nDescreva o que você estava fazendo:\n[Descreva aqui]\n\nObrigado!`
    );
    window.location.href = `mailto:suporte@masp.org.br?subject=${subject}&body=${body}`;
  };

  const handleRetry = () => {
    // Para chunk ausente, reset() re-renderia o mesmo grafo quebrado; só o reload recupera.
    if (chunkError) {
      window.location.reload();
      return;
    }
    reset();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleOpenInBrowser = () => {
    window.open(window.location.href, '_blank');
  };

  return (
    <div className='flex min-h-screen flex-col items-center justify-center bg-white px-4'>
      <div className='w-full max-w-md text-center'>
        {/* MASP Logo/Branding */}
        <div className='mb-8'>
          <div className='mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-red-50'>
            <AlertCircle className='h-10 w-10 text-red-600' />
          </div>
          <h1 className='text-3xl font-bold text-gray-900'>Ops! Algo deu errado</h1>
        </div>

        {/* Error Message */}
        <div className='mb-8 space-y-3'>
          <p className='text-lg text-gray-600'>
            Encontramos um problema inesperado. Não se preocupe, nossa equipe já foi notificada.
          </p>
          <p className='text-sm text-gray-500'>
            Você pode tentar novamente ou voltar para a página inicial. Se o problema persistir, entre em contato com
            nosso suporte.
          </p>
          {isInAppBrowser && (
            <div className='mt-4 rounded-lg bg-blue-50 p-3'>
              <p className='text-sm text-blue-800'>
                <strong>Dica:</strong> Você está usando o navegador do {browserName}. Para melhor experiência, tente
                abrir no seu navegador principal (Safari, Chrome, etc).
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className='mb-8 flex flex-col gap-3'>
          <button
            onClick={handleRetry}
            className='inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-6 py-3 font-medium text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2'>
            <RefreshCw className='h-5 w-5' />
            Tentar novamente
          </button>
          <button
            onClick={handleGoHome}
            className='inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-6 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2'>
            <Home className='h-5 w-5' />
            Ir para início
          </button>
          {isInAppBrowser && (
            <button
              onClick={handleOpenInBrowser}
              className='inline-flex items-center justify-center gap-2 rounded-lg border-2 border-blue-500 bg-blue-50 px-6 py-3 font-medium text-blue-700 transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'>
              <ExternalLink className='h-5 w-5' />
              Abrir no Navegador
            </button>
          )}
        </div>

        {/* In-App Browser Instructions */}
        {isInAppBrowser && (
          <div className='mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4'>
            <p className='mb-2 text-sm font-medium text-blue-900'>Como abrir no navegador:</p>
            <ul className='space-y-1 text-left text-sm text-blue-700'>
              <li>• Instagram: Toque nos 3 pontos (...) e selecione "Abrir no navegador"</li>
              <li>• Facebook: Toque no ícone do navegador no topo</li>
              <li>• Outros apps: Procure a opção de compartilhar e escolha seu navegador</li>
            </ul>
          </div>
        )}

        {/* Report Error */}
        <div className='border-t border-gray-200 pt-6'>
          <p className='mb-3 text-sm text-gray-500'>Precisa de ajuda?</p>
          <button
            onClick={handleReportError}
            className='text-sm font-medium text-red-600 underline-offset-2 hover:underline'>
            Reportar este problema
          </button>
        </div>

        {/* Error Digest (for debugging) */}
        {error.digest && (
          <div className='mt-8 rounded-lg bg-gray-50 p-4'>
            <p className='text-xs text-gray-400'>
              Código de referência: <code className='font-mono'>{error.digest}</code>
            </p>
          </div>
        )}

        {/* Footer */}
        <div className='mt-12 text-sm text-gray-400'>
          <p>MASP - Museu de Arte de São Paulo Assis Chateaubriand</p>
        </div>
      </div>
    </div>
  );
}
